import { useCallback, useContext, useRef, useState } from 'react'
import { exportMicroLearning } from '../../../api/api'
import { AppStateContext } from '../../../state/AppProvider'
import { downloadBlob, extractFilename } from '../../../utils/download'

export const useExport = (mlId: string) => {
  const [isExporting, setIsExporting] = useState(false)
  const isExportingRef = useRef(false)
  const appStateContext = useContext(AppStateContext)
  const dispatchRef = useRef(appStateContext?.dispatch)
  dispatchRef.current = appStateContext?.dispatch

  const triggerExportRef = useRef<() => Promise<void>>()

  const triggerExport = useCallback(async () => {
    if (isExportingRef.current) return
    isExportingRef.current = true
    setIsExporting(true)

    const notifyExportError = () =>
      dispatchRef.current?.({
        type: 'SET_SNACKBAR_STATE',
        payload: {
          message: 'Export failed. Please try again.',
          severity: 'error',
          action: { label: 'Retry', onClick: () => triggerExportRef.current?.() },
        },
      })

    try {
      const response = await exportMicroLearning(mlId)

      if (response.ok) {
        const blob = await response.blob()
        if (blob.size === 0) {
          notifyExportError()
          return
        }
        const fallbackName = `${mlId}_Micro-Learning_Export.xlsx`
        const filename = extractFilename(response, fallbackName)
        downloadBlob(blob, filename)
        dispatchRef.current?.({
          type: 'SET_SNACKBAR_STATE',
          payload: { message: 'Export downloaded successfully.', severity: 'success' },
        })
      } else {
        notifyExportError()
      }
    } catch {
      notifyExportError()
    } finally {
      isExportingRef.current = false
      setIsExporting(false)
    }
  }, [mlId])

  triggerExportRef.current = triggerExport

  return { isExporting, triggerExport }
}
